import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Package,
  Warehouse,
  ShoppingCart,
  Shield,
  BarChart3,
  Bell,
  User,
  Menu,
  X,
  ChevronDown,
  Settings,
  HelpCircle,
  LogOut,
  Truck,
  AlertTriangle,
  FileText,
  Calendar,
  TrendingUp,
  Activity,
  Zap,
  Building,
  DollarSign,
  FileText as InvoiceIcon,
  Users as DemographicIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  subItems?: SubNavigationItem[];
}

interface SubNavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  description: string;
}

const EnterpriseNavigation: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState<string | null>(null);
  const [mobileExpandedItems, setMobileExpandedItems] = useState<Set<string>>(new Set());
  const navRef = useRef<HTMLDivElement | null>(null);

  // Smooth open/close to prevent flicker when moving from tab to dropdown
  const closeTimeoutRef = useRef<number | null>(null);

  const openDropdown = (name: string) => {
    if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
    setHoveredDropdown(name);
  };

  const closeDropdown = () => {
    closeTimeoutRef.current = window.setTimeout(() => setHoveredDropdown(null), 120);
  };

  const toggleMobileItem = (itemName: string) => {
    setMobileExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(itemName) ? next.delete(itemName) : next.add(itemName);
      return next;
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setHoveredDropdown(null);
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation: NavigationItem[] = [
    {
      name: 'Dashboards',
      href: '/dashboards',
      icon: BarChart3,
      subItems: [
        { name: 'Executive Dashboard', href: '/dashboards', icon: BarChart3, description: 'High-level KPIs and business metrics' },
        { name: 'Orders Dashboard', href: '/dashboards/orders', icon: ShoppingCart, description: 'Order volume, fulfillment, and performance' },
        { name: 'Supply Chain Dashboard', href: '/dashboards/supply-chain', icon: Truck, description: 'Supplier performance and procurement KPIs' },
        { name: 'Compliance Dashboard', href: '/dashboards/compliance', icon: Shield, description: 'Regulatory compliance metrics and alerts' },
        { name: 'Financial Dashboard', href: '/dashboards/financial', icon: TrendingUp, description: 'Revenue, margins, and financial KPIs' },
        { name: 'Inventory Dashboard', href: '/dashboards/inventory', icon: Warehouse, description: 'Stock levels, turnover, and optimization' },
      ],
    },
    {
      name: 'Inventory',
      href: '/inventory',
      icon: Warehouse,
      subItems: [
        { name: 'Stock Levels', href: '/inventory', icon: Warehouse, description: 'Current inventory management' },
        { name: 'Medicine Catalog', href: '/catalog', icon: Package, description: 'Browse and manage medication inventory' },
        { name: 'Drug Interactions', href: '/catalog/interactions', icon: Zap, description: 'Check medication interactions' },
        { name: 'PBS Lookup', href: '/catalog/pbs', icon: FileText, description: 'PBS pricing and subsidies' },
        { name: 'Expiry Management', href: '/inventory/expiry', icon: Calendar, description: 'Track and manage expiring medications' },
      ],
    },
    {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingCart,
      subItems: [{ name: 'Order Management', href: '/orders', icon: ShoppingCart, description: 'Process and fulfill customer orders' }],
    },
    {
      name: 'Supply Chain',
      href: '/supply-chain',
      icon: Truck,
      subItems: [
        { name: 'Supplier Management', href: '/supply-chain', icon: Building, description: 'Manage supplier relationships' },
        { name: 'Purchase Orders', href: '/supply-chain/purchase-orders', icon: FileText, description: 'Create and track purchase orders' },
        { name: 'Procurement', href: '/supply-chain/procurement', icon: ShoppingCart, description: 'Procurement processes and approvals' },
        { name: 'Stock Transfers', href: '/supply-chain/transfers', icon: Truck, description: 'Inter-pharmacy stock movements' },
      ],
    },
    {
      name: 'Invoicing',
      href: '/invoicing',
      icon: InvoiceIcon,
      subItems: [
        { name: 'Invoice Processing', href: '/invoicing', icon: InvoiceIcon, description: 'Create and process invoices' },
        { name: 'Facility Billing', href: '/invoicing/facilities', icon: Building, description: 'Aged care and NDIS billing' },
        { name: 'Payment Tracking', href: '/invoicing/payments', icon: DollarSign, description: 'Track payments and outstanding amounts' },
      ],
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: FileText,
      subItems: [
        { name: 'Demographics', href: '/reports/demographics', icon: DemographicIcon, description: 'Patient demographic analysis' },
        { name: 'Financial Reports', href: '/reports/financial', icon: TrendingUp, description: 'Revenue and profitability reports' },
        { name: 'Operational Reports', href: '/reports/operational', icon: Activity, description: 'Operational efficiency reports' },
      ],
    },
    {
      name: 'Alerts',
      href: '/alerts',
      icon: Bell,
      subItems: [
        { name: 'Active Alerts', href: '/alerts', icon: AlertTriangle, description: 'Current system alerts requiring attention' },
        { name: 'Intelligent Ordering Alerts', href: '/alerts/ordering', icon: Zap, description: 'Smart ordering recommendations and alerts' },
        { name: 'Alert History', href: '/alerts/history', icon: Activity, description: 'Historical alert records and resolutions' },
      ],
    },
    {
      name: 'Compliance',
      href: '/compliance',
      icon: Shield,
      subItems: [
        { name: 'Regulatory Compliance', href: '/compliance', icon: Shield, description: 'Pharmacy Board compliance tracking' },
        { name: 'S8 Register', href: '/compliance/s8-register', icon: FileText, description: 'Controlled substances management' },
        { name: 'Audit Trail', href: '/compliance/audit', icon: Activity, description: 'System audit logs and compliance reports' },
      ],
    },
  ];

  const isActiveRoute = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-lg">
      <div className="w-full px-6">
        <div className="flex items-center justify-between h-14">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg">
                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  <path d="M8 10h2v2H8v-2zm4 0h2v2h-2v-2zm-2 2h2v2h-2v-2z" fill="white" opacity="0.8" />
                </svg>
              </div>
              <div>
                <h1 className="text-base font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
                  MedFast Pharmacy
                </h1>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => (
                <div
                  key={item.name}
                  className="relative flex-shrink-0 pb-2" // keep the space below inside the hover zone
                  onMouseEnter={() => (item.subItems ? openDropdown(item.name) : undefined)}
                  onMouseLeave={closeDropdown}
                >
                  <NavLink
                    to={item.href}
                    onClick={() => {
                      if (!item.subItems) setHoveredDropdown(null);
                    }}
                    className={({ isActive }) => {
                      const base =
                        'nav-item flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200';
                      const active =
                        'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 shadow-sm border border-purple-200';
                      const inactive =
                        'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:scale-105 hover:shadow-md';
                      return `${base} ${isActive || isActiveRoute(item.href) ? active : inactive}`;
                    }}
                    aria-expanded={item.subItems ? (hoveredDropdown === item.name) : undefined}
                    aria-haspopup={item.subItems ? 'menu' : undefined}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{item.name}</span>
                    {item.subItems && (
                      <ChevronDown
                        className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${
                          hoveredDropdown === item.name ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </NavLink>

                  {/* Dropdown Menu */}
                  {item.subItems && (
                    <div
                      className={`absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 py-2 z-[9999] ${
                        hoveredDropdown === item.name ? 'block' : 'hidden'
                      }`}
                      role="menu"
                    >
                      {item.subItems.map((subItem) => (
                        <NavLink
                          key={subItem.href}
                          to={subItem.href}
                          onClick={() => setHoveredDropdown(null)}
                          className={({ isActive }) =>
                            `flex items-start space-x-3 px-4 py-3 hover:bg-gray-50 transition-all duration-200 hover:scale-[1.02] cursor-pointer ${
                              isActive ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-r-2 border-purple-500' : ''
                            }`
                          }
                          role="menuitem"
                        >
                          <div className="p-2 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100">
                            <subItem.icon className="h-4 w-4 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{subItem.name}</p>
                            <p className="text-sm text-gray-500">{subItem.description}</p>
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-1">
            {/* Notifications */}
            <div className="relative">
              <button className="nav-item relative p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:scale-105">
                <Bell className="h-4 w-4" />
                <span className="absolute -top-0.5 -right-0.5 block h-4 w-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs flex items-center justify-center font-medium">
                  3
                </span>
              </button>
            </div>

            {/* Quick Actions */}
            <div className="hidden lg:flex items-center space-x-1">
              <button className="nav-item p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:scale-105">
                <Settings className="h-4 w-4" />
              </button>
              <button className="nav-item p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:scale-105">
                <HelpCircle className="h-4 w-4" />
              </button>
            </div>

            {/* User Profile */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="nav-item flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-50 transition-all duration-200 hover:scale-105"
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
              >
                <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="hidden sm:block text-left min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize truncate">
                    {user?.role} • {user?.branch}
                  </p>
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" aria-hidden="true" />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-200 py-2 z-50" role="menu">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <p className="text-xs text-gray-400 mt-1">{user?.branch}</p>
                  </div>
                  <div className="py-2">
                    <button className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left">
                      <User className="h-4 w-4" />
                      <span>Profile Settings</span>
                    </button>
                    <button className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left">
                      <Settings className="h-4 w-4" />
                      <span>Preferences</span>
                    </button>
                    <button className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left">
                      <HelpCircle className="h-4 w-4" />
                      <span>Help &amp; Support</span>
                    </button>
                  </div>
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      onClick={logout}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 md:hidden transition-all duration-200 hover:scale-105"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-6 py-4 space-y-2">
            {navigation.map((item) => (
              <div key={item.name}>
                <div className="flex items-center">
                  <NavLink
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex-1 ${
                        isActive || isActiveRoute(item.href)
                          ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </NavLink>
                  {item.subItems && (
                    <button
                      onClick={() => toggleMobileItem(item.name)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200"
                      aria-label={`Toggle ${item.name} sub menu`}
                      aria-expanded={mobileExpandedItems.has(item.name)}
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          mobileExpandedItems.has(item.name) ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </div>

                {/* Mobile Sub Items */}
                {item.subItems && mobileExpandedItems.has(item.name) && (
                  <div className="ml-8 mt-2 space-y-1">
                    {item.subItems.map((subItem) => (
                      <NavLink
                        key={subItem.href}
                        to={subItem.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:scale-[1.02] cursor-pointer ${
                            isActive ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-50'
                          }`
                        }
                      >
                        <subItem.icon className="h-4 w-4" />
                        <span>{subItem.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default EnterpriseNavigation;
